/**
 * @file Base Editor component with shared logic for all JSON editors
 * @version 1.0.0
 */

import { applySchemaCustomizations } from './schemas.js';
import { createGetAttribute, DEFAULTS_KEY } from './attributes.js';
import { useEditableObject } from './editable.js';
import { resolveEditor } from './component-registry.js';
import { getSchemaDefaultValue } from './schemas.js';

export { DEFAULTS_KEY };

/**
 * Base editor component providing common functionality for all editor types.
 * Use `extends: BaseEditor` in child components.
 *
 * Includes: permission checks, attribute resolution, edit state management,
 * and value synchronization.
 */
export default {
  name: 'BaseEditor',

  setup(props) {
    const injectedDefaults = Vue.inject(DEFAULTS_KEY, Vue.ref({}));

    const mergedDefaults = Vue.computed(() => ({
      ...Vue.unref(injectedDefaults),
      ...props.defaults,
    }));

    Vue.provide(DEFAULTS_KEY, mergedDefaults);

    // Provide self to child components
    const instance = Vue.getCurrentInstance();
    Vue.provide('editor', instance.proxy);

    // Create the editable object once per component instance using reactive getters.
    // Calling useEditableObject inside a computed property would leak the watchers it
    // creates on every re-evaluation; keeping it here scopes them to the component lifecycle.
    const valueRef = Vue.computed(() => props.value);
    const schemaRef = Vue.computed(() =>
      applySchemaCustomizations(props.schema, props.customize)
    );
    const ownEditable = useEditableObject(valueRef, { schema: schemaRef });

    return {
      mergedDefaults,
      ownEditable,
    };
  },

  inheritAttrs: false,

  props: {
    schema: {
      type: Object,
      required: true,
    },
    value: {
      required: true,
    },
    reference: {},
    mode: {
      type: String,
      default: 'display',
      validator(mode) {
        return [
          'display',
          'edit',
          'field-click-edit',
          'form',
          'click-edit',
        ].includes(mode);
      },
    },
    customize: {
      type: Object,
      default: () => ({}),
    },
    displayLabel: {
      type: Boolean,
      default: undefined,
    },
    defaults: {
      type: Object,
      default: () => ({}),
    },
    controlStyles: {
      type: Object,
      default: undefined,
    },
  },

  emits: ['input', 'update:value'],

  data() {
    return {
      isEditing: false,
      childStates: new Map(),
    };
  },

  computed: {
    /**
     * Get customized schema with all customizations applied
     */
    customizedSchema() {
      return applySchemaCustomizations(this.schema, this.customize);
    },

    internalValue() {
      return this.ownEditable;
    },

    /**
     * Editor title from schema attributes
     */
    editorTitle() {
      return this.getAttribute('title') || this.getAttribute('name') || null;
    },

    /**
     * Context-bound getAttribute function
     */
    getAttribute() {
      return createGetAttribute(
        this.customizedSchema,
        this.$props,
        this.$attrs,
        this.mergedDefaults
      );
    },

    /**
     * Check if user has permission to read
     */
    canRead() {
      return this.getAttribute('readable', true);
    },

    /**
     * Check if user has permission to edit
     */
    canEdit() {
      return this.getAttribute('editable', true);
    },

    /**
     * Check if user has permission to delete
     */
    canDelete() {
      return (
        this.getAttribute('showDeleteButton', false) &&
        this.getAttribute('deletable', true)
      );
    },

    /**
     * Check if current value is undefined/null
     */
    isUndefined() {
      const val = this.internalValue.editable;
      return val === undefined || val === null;
    },

    /**
     * First validation error as human readable string
     */
    validationError() {
      return this.internalValue.validationErrors?.[0] ?? null;
    },

    /**
     * Mode to pass to child editors
     */
    childMode() {
      if (!this.canEdit) return 'display';
      switch (this.mode) {
        case 'display':
          return 'display';
        case 'edit':
          return 'edit';
        case 'field-click-edit':
          return 'click-edit';
        case 'form':
          return 'edit';
        case 'click-edit':
          return this.isEditing ? 'edit' : 'display';
        default:
          return 'display';
      }
    },

    /**
     * Check if there are any changes
     */
    hasChanges() {
      return this.internalValue.isModified;
    },

    /**
     * Check if there are any errors
     */
    hasErrors() {
      return (
        (this.internalValue.editable !== undefined &&
          !this.internalValue.isValid) ||
        this.childStates.size > 0
      );
    },

    /**
     * Can save (valid changes exist)
     */
    canSave() {
      return this.hasChanges && !this.hasErrors;
    },

    /**
     * Check if in editing state (for click-edit/form controls)
     */
    isInEditMode() {
      return (
        this.mode === 'edit' ||
        this.mode === 'form' ||
        (this.mode === 'click-edit' && this.isEditing)
      );
    },

    /**
     * Check if controls should be visible
     */
    showControls() {
      return this.canEdit && this.isInEditMode;
    },

    /**
     * Whether to display button labels
     */
    displayButtonsLabels() {
      return this.getAttribute('displayButtonsLabels', false);
    },

    /**
     * Show click-edit trigger (pencil button)
     */
    showClickEditTrigger() {
      return (
        this.mode === 'click-edit' &&
        !this.isEditing &&
        !this.isUndefined &&
        this.canEdit
      );
    },

    /**
     * Show add button for undefined value
     */
    showAddButton() {
      return this.isUndefined && this.canEdit && this.mode !== 'display';
    },

    /**
     * Show save button
     */
    showSaveButton() {
      return (
        (this.mode === 'form' ||
          (this.mode === 'click-edit' && this.isEditing)) &&
        this.canEdit
      );
    },

    /**
     * Show reset button (form mode)
     */
    showResetButton() {
      return this.mode === 'form';
    },

    /**
     * Show cancel button (click-edit mode)
     */
    showCancelButton() {
      return this.mode === 'click-edit' && this.isEditing;
    },

    /**
     * Show delete button
     */
    showDeleteButton() {
      return (
        this.canDelete &&
        !this.isUndefined &&
        !(
          this.mode === 'display' ||
          (this.mode === 'click-edit' && !this.isEditing)
        )
      );
    },

    /**
     * Check if in active editing state (for control visibility)
     */
    isActiveEditing() {
      return !this.isUndefined;
    },
  },

  watch: {
    externalState: {
      handler() {
        if (this.mode !== 'click-edit' && this.mode !== 'form') {
          this.emitState();
        }
      },
    },
  },

  methods: {
    /**
     * Add value when undefined
     */
    addValue() {
      this.internalValue.editable = getSchemaDefaultValue(
        this.customizedSchema,
        this.getAttribute('generateDefaultsForOptional', true)
      );
    },

    /**
     * Reset to original value
     */
    reset() {
      this.internalValue.reset();
      this.isEditing = false;
      this.childStates.forEach(state => state.sender?.reset?.());
      this.childStates.clear();
    },

    /**
     * Start editing (click-edit mode)
     */
    startEdit() {
      this.isEditing = true;
    },

    /**
     * Cancel editing
     */
    cancelEdit() {
      this.reset();
    },

    /**
     * Save changes
     */
    saveEdit() {
      if (this.canSave) {
        this.isEditing = false;
        this.emitState();
      }
    },

    /**
     * Delete/clear value
     */
    handleDelete() {
      this.internalValue.editable = undefined;
    },

    /**
     * Emit current state to parent
     */
    emitState() {
      const state = this.externalState;
      this.$emit('input', state);
      if (state.state === 'updated' && state.update) {
        this.$emit('update:value', state.update(this.value));
      }
    },

    /**
     * Get component for nested editor
     * @param itemSchema
     */
    getItemComponent(itemSchema) {
      return resolveEditor(itemSchema, this.mergedDefaults);
    },
  },
};
